/**
 * NOTIFICATION BELL COMPONENT
 */

import { useState } from 'react';
import { Bell } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { NotificationsService } from '@/services/NotificationServices';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export default function NotificationBell() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  // Query pour le count de notifications non lues
  const { data: unreadCount = 0, refetch } = useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: () => NotificationsService.getUnreadCount(),
    refetchInterval: 30000, // Polling toutes les 30 secondes
  });

  // Query pour les notifications récentes (quand dropdown ouvert)
  const { data: recentNotifications = [], isLoading } = useQuery({
    queryKey: ['notifications-recent'],
    queryFn: () => NotificationsService.getUnreadNotifications(),
    enabled: isOpen, // Charge seulement si dropdown ouvert
  });

  // Marquer notification comme lue
  const handleMarkAsRead = async (id: string) => {
    try {
      await NotificationsService.markAsRead(id);
      refetch();
      toast.success('Notification marquée comme lue');
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Marquer toutes comme lues
  const handleMarkAllAsRead = async () => {
    try {
      await NotificationsService.markAllAsRead();
      refetch();
      toast.success('Toutes les notifications marquées comme lues');
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-80">
        <div className="flex items-center justify-between px-4 py-2 border-b">
          <h3 className="font-semibold">Notifications</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllAsRead}
              className="text-xs"
            >
              Tout marquer comme lu
            </Button>
          )}
        </div>

        <div className="max-h-96 overflow-y-auto">
          {isLoading ? (
            <div className="p-4 text-center text-muted-foreground">
              Chargement...
            </div>
          ) : recentNotifications.length > 0 ? (
            recentNotifications.slice(0, 10).map((notification) => {
              const formatted = NotificationsService.formatNotification(notification);
              return (
                <DropdownMenuItem
                  key={notification.id}
                  className="flex flex-col items-start p-4 cursor-pointer hover:bg-accent"
                  onClick={() => {
                    handleMarkAsRead(notification.id);
                    if (notification.action_url) {
                      navigate(notification.action_url);
                    }
                  }}
                >
                  <div className="flex items-start gap-2 w-full">
                    <span className={`text-lg ${formatted.color}`}>
                      {formatted.icon}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{notification.title}</p>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                        {notification.message}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {NotificationsService.getRelativeTime(notification.created_at)}
                      </p>
                    </div>
                  </div>
                </DropdownMenuItem>
              );
            })
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              <Bell className="h-12 w-12 mx-auto mb-2 opacity-20" />
              <p className="text-sm">Aucune notification</p>
            </div>
          )}
        </div>

        {recentNotifications.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-center cursor-pointer"
              onClick={() => navigate('/notifications')}
            >
              Voir toutes les notifications
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
